import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import HorseCard from "../components/HorseCard.jsx";
import Ticker from "../components/Ticker.jsx";
import { Search, Send, Upload, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const COLOR_OPTIONS = ["Хүрэн", "Халзан", "Шарга", "Хээр", "Бор", "Цагаан", "Хар", "Зээрд"];
const GENDER_OPTIONS = ["Азарга", "Гүү", "Үрээ", "Унага", "Даага", "Шүдлэн", "Хязаалан", "Соёолон"];

const Landing = () => {
  const [q, setQ] = useState("");
  const [horses, setHorses] = useState([]);
  const [forSaleHorses, setForSaleHorses] = useState([]);
  const [forSaleTotal, setForSaleTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (q && q.trim()) {
        setSearching(true);
        const { data } = await api.get("/horses", {
          params: { q: q, limit: 24 },
        });
        setHorses(data.items || []);
        setTotal(data.total || 0);
      } else {
        setSearching(false);
        const { data } = await api.get("/horses/for-sale");
        const list = data || [];
        setForSaleHorses(list);
        setForSaleTotal(list.length);
      }
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load();
    const el = document.querySelector("#listings");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Top Ticker */}
      <Ticker />

      {/* HERO / HOME */}
      <section id="home" className="relative overflow-hidden border-b border-[#B8860B]/20">
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/36710489/pexels-photo-36710489.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FBF9F6]/70 to-[#FBF9F6]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12 md:pt-16 md:pb-20 text-center">
          <img src="/logo.png" alt="Дарьганга зээрднүүд" className="h-40 md:h-56 w-auto object-contain mx-auto mb-6 fade-up" data-testid="hero-logo" />

          {/* Тагтай зураг (хашаа дахь адуу) */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B3B5A] leading-tight max-w-3xl mx-auto fade-up" data-testid="hero-title">
            Дарьганга Зээрднүүд хурдан адуу, үржлийн аж ахуй
          </h1>
          <div className="mt-6 max-w-3xl mx-auto fade-up-delay-1">
            <img
              src="/hashaa_aduu.jpg"
              alt="Хашаан дахь Дарьганга зээрднүүд адуу"
              className="w-full rounded-2xl shadow-lg border border-[#B8860B]/30 object-cover"
              data-testid="hero-corral-image"
            />
          </div>
          <p className="mt-6 text-base md:text-lg text-[#2C241B] max-w-3xl mx-auto fade-up-delay-1 leading-relaxed" data-testid="hero-description">
            Энэхүү цахим хуудсанд зөвхөн манай үржлийн аж ахуйд төрсөн адууны мэдээлэл байршсан болно.
          </p>

          {/* Google-like big search */}
          <form
            onSubmit={onSearch}
            className="mt-8 max-w-2xl mx-auto fade-up-delay-2"
            data-testid="hero-search-form"
          >
            <div className="bg-white border border-[#D6CFC4] rounded-full shadow-md hover:shadow-lg focus-within:border-[#B8860B] focus-within:shadow-lg transition-all flex items-center pl-5 pr-2 py-1">
              <Search size={20} className="text-[#5C4F42]" strokeWidth={1.5} />
              <input
                type="text"
                id="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Бүртгэлийн дугаар, Микрочип, эсвэл нэрээр хайх..."
                className="flex-1 bg-transparent px-4 py-3 text-[#2C241B] focus:outline-none text-base"
                data-testid="hero-search-input"
              />
              <button
                type="submit"
                className="bg-[#1B3B5A] text-white hover:bg-[#264F78] rounded-full px-6 py-2.5 text-sm font-bold transition-colors"
                data-testid="hero-search-submit"
              >
                Хайх
              </button>
            </div>
          </form>

          {/* Stats */}
          {stats && (
            <div className="mt-10 grid grid-cols-2 gap-3 max-w-md mx-auto fade-up-delay-2">
              <StatBox label="Бүртгэгдсэн адуу" value={stats.total_horses} />
              <StatBox label="Зарагдаж буй" value={stats.for_sale_count} />
            </div>
          )}
        </div>
      </section>

      {/* SEARCH anchor (jumps to listings) */}
      <span id="search" className="block" />

      {/* LISTINGS */}
      <section id="listings" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {searching ? (
          <>
            <SectionHeader label="Хайлтын үр дүн" title="Адуунууд" />
            {!loading && horses.length > 0 && (
              <div className="text-center text-xs text-[#5C4F42] -mt-4 mb-6" data-testid="listings-total">Нийт {total} адуу</div>
            )}
            {loading ? (
              <div className="text-center py-16 text-[#5C4F42]">Ачааллаж байна...</div>
            ) : horses.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-2xl text-[#1B3B5A] font-bold mb-2">Адуу олдсонгүй</div>
                <p className="text-[#5C4F42]">Өөр түлхүүр үгээр хайж үзнэ үү.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="horses-grid">
                {horses.map((h) => <HorseCard key={h.id} horse={h} />)}
              </div>
            )}
          </>
        ) : (
          <>
            <SectionHeader label="Зар" title="Манай аж ахуйн зарагдаж буй адуу" />
            {loading ? (
              <div className="text-center py-16 text-[#5C4F42]">Ачааллаж байна...</div>
            ) : forSaleHorses.length === 0 ? (
              <div className="text-center py-12 text-[#5C4F42]" data-testid="no-for-sale">
                Одоогоор зарагдаж буй адуу алга байна.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="for-sale-grid-landing">
                  {forSaleHorses.slice(0, 4).map((h) => <HorseCard key={h.id} horse={h} />)}
                </div>
                {forSaleTotal > 4 && (
                  <div className="text-center mt-8">
                    <Link
                      to="/sale"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B3B5A] hover:bg-[#B8860B] text-white font-bold rounded transition-colors text-sm"
                      data-testid="view-all-for-sale"
                    >
                      Өөр зарагдаж байгаа адууг харах
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-[#F0EBE1]/50 border-y border-[#B8860B]/20 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <SectionHeader label="Бидний тухай" title="Дарьганга зээрднүүд" left />
            <div className="text-[#2C241B] leading-relaxed space-y-3 text-[15px]" data-testid="about-text">
              <p>
                Сайн байна уу? Морь хурдан, уяа эвтэй байна уу? Дарьганга Зээрднүүд хурдан адуу,
                үржлийн аж ахуйн албан ёсны цахим хуудсанд тавтай морил.
              </p>
              <p>
                Манай үржлийн аж ахуй нь угшил гарал нь үнэн зөв, тодорхой хэв шинж бүхий адуун
                сүргийг бүрдүүлж, Монголын адуу үржлийн салбарт ил тод, хариуцлагатай жишиг
                тогтоохоор, өөрийн үржлийн адууны удмыг ам дамжсан ярианд бус үнэн зөв бүртгэл,
                мэдээлэлд тулгуурлан хөтлөхийг эрхэмлэн, нийтэд нээлттэй мэдээллийн сан бүрдүүлэн
                ажиллаж байна.
              </p>
              <p>
                Манай эрхэм зорилго бол зөвхөн адуу өсгөн үржүүлэх биш, харин үеэс үед дамжих үнэт
                удам, танигдахуйц хэв шинж, давтагдах чанартай үржлийн сүргийг бий болгох явдал юм.
              </p>
              <p>
                Энэхүү цахим хуудас нь манай үржлийн аж ахуйн унаган адууны албан ёсны мэдээллийн
                сан бөгөөд адуу бүрийн угшил, гарал, үр төлийн мэдээллийг үнэн зөв хадгалж, ирээдүйд
                үнэ цэнтэй өв болон үлдээх болно.
              </p>
              <p className="pt-2 font-bold text-[#1B3B5A] italic">
                Дарьганга Зээрднүүд овогтой Ганбаатарын Дөлбадрах
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="/sunset_aduu.jpg"
              alt="Нар жаргахад Дарьганга зээрднүүд"
              className="w-full rounded-lg shadow-lg border border-[#D6CFC4] object-cover"
              data-testid="about-image"
            />
          </div>
        </div>
      </section>

      {/* SUBMIT */}
      <SubmitSection />

      {/* CONTACT handled in footer */}
    </>
  );
};

const SectionHeader = ({ label, title, left = false }) => (
  <div className={`mb-6 ${left ? "" : "text-center"}`}>
    <div className="text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-2 font-bold">{label}</div>
    <h2 className="text-2xl md:text-3xl font-bold text-[#1B3B5A]">{title}</h2>
    {!left && <div className="ornament-divider mt-4">✦</div>}
  </div>
);

const StatBox = ({ label, value }) => (
  <div className="bg-white border border-[#D6CFC4] rounded-lg p-3">
    <div className="text-2xl font-bold text-[#1B3B5A]">{value ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C4F42] mt-1">{label}</div>
  </div>
);

const SubmitSection = () => {
  const [form, setForm] = useState({
    submitter_name: "", submitter_phone: "",
    sale_price: "",
    horse_name: "", birth_year: "", color: "", gender: "", microchip: "", brand_stamp: "",
    registration_number: "", country: "", owner_name: "", breeder_farm: "",
    father_name: "", mother_name: "", description: "",
  });
  const [photoPath, setPhotoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Зураг 10MB-аас их байна");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/submissions/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.photo_path);
      toast.success("Зураг орлоо");
    } catch {
      toast.error("Зураг оруулахад алдаа гарлаа");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.submitter_name.trim() || !form.horse_name.trim()) {
      toast.error("Таны нэр болон адууны нэр шаардлагатай");
      return;
    }
    if (!form.submitter_phone.trim()) {
      toast.error("Холбогдох утасны дугаар шаардлагатай");
      return;
    }
    if (!form.sale_price.trim()) {
      toast.error("Зарах үнэ шаардлагатай");
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      birth_year: form.birth_year ? Number(form.birth_year) : null,
      photo_path: photoPath,
    };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" && !["submitter_phone", "sale_price"].includes(k)) payload[k] = null;
    });
    try {
      await api.post("/submissions", payload);
      toast.success("Амжилттай илгээгдлээ! Админ удахгүй танд хариу өгнө.");
      setForm({
        submitter_name: "", submitter_phone: "",
        sale_price: "",
        horse_name: "", birth_year: "", color: "", gender: "", microchip: "", brand_stamp: "",
        registration_number: "", country: "", owner_name: "", breeder_farm: "",
        father_name: "", mother_name: "", description: "",
      });
      setPhotoPath(null);
    } catch {
      toast.error("Илгээхэд алдаа гарлаа");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="submit" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <SectionHeader label="Зарах адууны зар илгээх" title="Өөрийн зарах адууны мэдээллийг (зар) илгээх хэсэг" />
      <p className="text-center text-sm text-[#5C4F42] max-w-2xl mx-auto -mt-2 mb-8">
        Энэ хэсэг нь зөвхөн <b>зарах адууны мэдээлэл</b> хүлээж авах зориулалттай. Админ шалгаад зарын
        ticker болон нийтийн санд бүртгэнэ. <span className="text-[#8B2500] font-bold">Зарах үнэ</span> болон{" "}
        <span className="text-[#8B2500] font-bold">холбогдох утасны дугаар</span> заавал бөглөнө.
      </p>

      <form onSubmit={onSubmit} className="bg-white border border-[#D6CFC4] rounded-lg p-6 md:p-8 shadow-sm space-y-5" data-testid="submit-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Таны нэр *" value={form.submitter_name} onChange={(v) => set("submitter_name", v)} required testId="sub-name" />
          <Input label="Холбогдох утасны дугаар *" value={form.submitter_phone} onChange={(v) => set("submitter_phone", v)} required testId="sub-phone" placeholder="99xxxxxx" />
          <Input label="Зарах үнэ *" value={form.sale_price} onChange={(v) => set("sale_price", v)} required testId="sub-sale-price" placeholder="жишээ: 25 сая ₮" />
        </div>
        <hr className="border-[#D6CFC4]" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Адууны нэр *" value={form.horse_name} onChange={(v) => set("horse_name", v)} required testId="sub-horse-name" />
          <Input label="Төрсөн он" type="number" value={form.birth_year} onChange={(v) => set("birth_year", v)} testId="sub-year" />
          <Select label="Зүс" value={form.color} onChange={(v) => set("color", v)} options={COLOR_OPTIONS} testId="sub-color" />
          <Select label="Хүйс" value={form.gender} onChange={(v) => set("gender", v)} options={GENDER_OPTIONS} testId="sub-gender" />
          <Input label="Микрочип" value={form.microchip} onChange={(v) => set("microchip", v)} testId="sub-microchip" />
          <Input label="Тамга (текстээр)" value={form.brand_stamp} onChange={(v) => set("brand_stamp", v)} testId="sub-brand" placeholder="Жишээ: Б-1" />
          <Input label="Бүртгэлийн дугаар" value={form.registration_number} onChange={(v) => set("registration_number", v)} testId="sub-reg" />
          <Input label="Улс" value={form.country} onChange={(v) => set("country", v)} testId="sub-country" />
          <Input label="Эзэмшигч" value={form.owner_name} onChange={(v) => set("owner_name", v)} testId="sub-owner" />
          <Input label="Үржүүлэгч (Аж ахуй)" value={form.breeder_farm} onChange={(v) => set("breeder_farm", v)} testId="sub-breeder" />
          <Input label="Эцгийн нэр" value={form.father_name} onChange={(v) => set("father_name", v)} testId="sub-father" />
          <Input label="Эхийн нэр" value={form.mother_name} onChange={(v) => set("mother_name", v)} testId="sub-mother" />
        </div>
        <div>
          <label className="text-sm font-bold text-[#5C4F42] mb-1 block">Нэмэлт мэдээлэл</label>
          <textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={3}
            className="w-full border border-[#D6CFC4] rounded px-3 py-2 focus:outline-none focus:border-[#B8860B]"
            data-testid="sub-description"
          />
        </div>
        <div>
          <label className="text-sm font-bold text-[#5C4F42] mb-1 block">Адууны зураг</label>
          <div className="flex items-center gap-4">
            {photoPath && <div className="text-xs text-[#059669]">✓ Зураг орлоо</div>}
            <label className="inline-flex items-center gap-2 bg-[#B8860B] text-white hover:bg-[#996F09] px-4 py-2 rounded cursor-pointer text-sm font-bold transition-colors">
              <Upload size={16} strokeWidth={1.5} />
              {uploading ? "Оруулж байна..." : photoPath ? "Зураг солих" : "Зураг оруулах"}
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="sub-photo" />
            </label>
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1B3B5A] text-white hover:bg-[#264F78] disabled:opacity-60 py-3 rounded font-bold transition-colors"
          data-testid="sub-submit"
        >
          <Send size={16} strokeWidth={1.5} />
          {submitting ? "Илгээж байна..." : "Зар илгээх"}
        </button>
      </form>
    </section>
  );
};

const Input = ({ label, value, onChange, type = "text", required, testId, placeholder }) => (
  <div>
    <label className="text-sm font-bold text-[#5C4F42] mb-1 block">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full border border-[#D6CFC4] rounded px-3 py-2 focus:outline-none focus:border-[#B8860B]"
      data-testid={testId}
    />
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-sm font-bold text-[#5C4F42] mb-1 block">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#D6CFC4] rounded px-3 py-2 focus:outline-none focus:border-[#B8860B]"
      data-testid={testId}
    >
      <option value="">— Сонгох —</option>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </div>
);

// Hidden id anchor for "for-sale" ticker section (scrolls to ticker area)
export const ForSaleAnchor = () => <div id="for-sale" />;

export default Landing;
